<template>
    <div v-if="amchartsPresent && mapDataPresent" class="c-svg-map">
        <div v-if="mapSettings.showAccessibleStateSelector" class="d-flex pb-2">
            <select id="svg-map-state-selector" class="c-svg-map__state-selector mr-3" ref="stateselector" aria-label="Select a state to display details.">
                <option value="" v-html="mapSettings.blankSelectorOptionLabel"></option>
                <option v-for="state in statesList" v-html="state.name" :key="state.id" :value="state.id"></option>
            </select>

            <button class="btn btn-primary c-svg-map__state-selector-button" @click="selectState">{{ mapSettings.selectorButtonLabel }}</button>
        </div>

        <div ref="chartdiv" :style="{ height: this.mapSettings.mapHeight }"></div>
    </div>
    
    <div v-else-if="!amchartsPresent && mapDataPresent" class="c-svg-map__error">
        <p>JavaScript Mapping Code Not Present. Could not draw the map.</p>
    </div>

    <div v-else-if="amchartsPresent && !mapDataPresent" class="c-svg-map__error">
        <p>Mapping data not present. Could not draw the map.</p>
    </div>
</template>

<script>
export default {
    props: {
        mapData: {
            type: Array,
            required: false
        },
        mapSettings: {
            type: Object,
            required: false,
            default() {
                return {
                    mapHeight: '500px',
                    activeStateColor: '#ce2a23',
                    inactiveStateColor: '#6f859a',
                    hiddenStateAbbrev: ['MD', 'RI', 'CT', 'MA', 'NH', 'DC', 'DE', 'NJ', 'VT'],
                    showAccessibleStateSelector: true,
                    screenReaderMapTitle: 'CPR In Schools Legislation Map',
                    selectorButtonLabel: 'Select',
                    blankSelectorOptionLabel: 'Select A State'
                }
            }
        }
    },
    data: function() {
        return {
            chart: null,
            statesList: [],
        };
    },
    computed: {
        amchartsPresent() {
            return window.am4core ? true : false;
        },
        mapDataPresent() {
            return Array.isArray(this.mapData) ? (this.mapData.length > 0 ? true : false) : false;
        } 
    },
    mounted() {
        if(this.amchartsPresent) {
            am4core.ready(() => {
                this.initMap();
            });
        }
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
        }
    },
    methods: {
        initMap() {
            // Themes begin
            am4core.useTheme(am4themes_animated);
            // Themes end

            // Create map instance
            var chart = am4core.create(this.$refs.chartdiv, am4maps.MapChart);
            chart.readerTitle = this.mapSettings.screenReaderMapTitle;
            chart.role = null;
            chart.svgContainer.hideOverflow = true;

            // Set map definition
            chart.geodata = am4geodata_usaLow;

            // Set projection
            chart.projection = new am4maps.projections.AlbersUsa();
            // chart.projection.role = '';

            // Set focus styles
            chart.focusFilter.strokeWidth = 0;

            // Create map polygon series
            var polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
            polygonSeries.readerTitle = '';
            polygonSeries.role = '';
            polygonSeries.dataFields.line1 = 'line1';
            polygonSeries.dataFields.line2 = 'line2';
            polygonSeries.dataFields.line3 = 'line3';
            polygonSeries.dataFields.url = 'url';
            polygonSeries.sortPolygonsBy = 'name';

            polygonSeries.heatRules.push({
                property: "fill",
                target: polygonSeries.mapPolygons.template,
                min: am4core.color(this.mapSettings.inactiveStateColor),
                max: am4core.color(this.mapSettings.activeStateColor),
            });

            // Make map load polygon data (state shapes and names) from GeoJSON
            polygonSeries.useGeodata = true;
            polygonSeries.calculateVisualCenter = true;

            // Set heatmap values for each state
            polygonSeries.data = this.mapData;

            // Create series tooltip
            polygonSeries.tooltip.getFillFromObject = false;
            polygonSeries.tooltip.background.fill = am4core.color("#fff");
            polygonSeries.tooltip.background.fillOpacity = 1;
            polygonSeries.tooltip.background.cornerRadius = 4;
            polygonSeries.tooltip.background.strokeWidth = 0;
            polygonSeries.tooltip.label.fill = am4core.color("#000");
            polygonSeries.tooltip.fillOpacity = 1;
            polygonSeries.tooltip.label.interactionsEnabled = true;

            if(window.innerWidth >= 1024) {
                polygonSeries.tooltip.fontSize = '0.8rem';
            } else {
                polygonSeries.tooltip.fontSize = '0.7rem';
                polygonSeries.tooltip.keepTargetHover = true;
                chart.homeZoomLevel = 1.1;
                polygonSeries.tooltip.label.maxWidth = 250;
                polygonSeries.tooltip.label.wrap = true;
            }

            // Configure series tooltip
            var polygonTemplate = polygonSeries.mapPolygons.template;
            polygonTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer;
            polygonTemplate.tooltipPosition = 'fixed';
            polygonTemplate.hoverOnFocus = true;
            
            if(window.innerWidth >= 1024) {
                polygonTemplate.tooltipText = "[bold]{name}\n[/]{line1}\n{line2}\n{line3}";
            } else {
                // Caution, setting custom tooltip HTML breaks IE support, use for mobile only
                polygonTemplate.tooltipHTML = `
                    <p class="c-svg-map__tooltip-header">{name} <a href='{url}' target='_blank'>(Link)</a></p>
                    <span class="c-svg-map__tooltip-content-line">{line1}</span>
                    <span class="c-svg-map__tooltip-content-line">{line2}</span>
                    <span class="c-svg-map__tooltip-content-line">{line3}</span>
                `
                polygonTemplate.userClassName = 'c-svg-map__disable-clicks';
            }

            polygonTemplate.nonScalingStroke = true;
            polygonTemplate.strokeWidth = 1.5;

            // Create series labels
            var labelSeries = chart.series.push(new am4maps.MapImageSeries());
            labelSeries.readerTitle = '';
            labelSeries.role = '';
            var labelTemplate = labelSeries.mapImages.template.createChild(am4core.Label);
            labelTemplate.horizontalCenter = "middle";
            labelTemplate.verticalCenter = "middle";
            labelTemplate.fill = am4core.color("#fff");
            labelTemplate.fontWeight = "bold";
            labelTemplate.interactionsEnabled = false;

            if(window.innerWidth >= 1024) {
                labelTemplate.fontSize = ".9rem";
            } else {
                labelTemplate.fontSize = ".7rem";
            }

            polygonSeries.events.on("inited", () => {
                polygonSeries.mapPolygons.each(polygon => {
                    if(polygon.dataItem.dataContext.id === undefined) {
                        return
                    }

                    this.statesList.unshift({
                        id: polygon.dataItem.dataContext.id,
                        name: polygon.dataItem.dataContext.name
                    });

                    var label = labelSeries.mapImages.create();
                    label.tabindex = -1;
                    var stateAbbrev = polygon.dataItem.dataContext.id.split("-").pop();
                    var value = polygon.dataItem.dataContext.value;
                    var hiddenStateIndex = this.mapSettings.hiddenStateAbbrev.indexOf(stateAbbrev);
                    polygon.readerTitle = `${polygon.dataItem.dataContext.name}: `;

                    if(polygon.dataItem.dataContext.line1) {
                        polygon.readerTitle += polygon.dataItem.dataContext.line1;
                    }

                    if(polygon.dataItem.dataContext.line2) {
                        polygon.readerTitle += polygon.dataItem.dataContext.line2;
                    }

                    if(polygon.dataItem.dataContext.line3) {
                        polygon.readerTitle += polygon.dataItem.dataContext.line3;
                    }

                    if(hiddenStateIndex > -1) {
                        label.background = new am4core.RoundedRectangle();
                        label.background.cornerRadius(4, 4, 4, 4);
                        label.ignoreBounds = true;

                        if(value === '1') {
                            label.background.fill = am4core.color(this.mapSettings.activeStateColor);
                        } else if( value === '0') {
                            label.background.fill = am4core.color(this.mapSettings.inactiveStateColor);
                        }

                        label.fontWeight = 'bold';
                        label.textAlign = 'middle';
                        label.textValign = 'middle';
                        label.contentAlign = 'middle';
                        label.contentValign = 'middle';
                        label.children.getIndex(0).text = stateAbbrev;
                        label.cursorOverStyle = am4core.MouseCursorStyle.pointer;

                        if(window.innerWidth < 576) {
                            label.fontSize = '.7rem';
                            label.width = 25;
                            label.height = 13;
                            label.paddingTop = 7;
                            label.paddingLeft = 13;

                            if(hiddenStateIndex < 5) {
                                let lat = 35.00 + (hiddenStateIndex * 2);
                                let lng = -70.71 + (hiddenStateIndex * .7);
                                label.latitude = lat;
                                label.longitude = lng;
                            } else {
                                let lat = 23.93 + (hiddenStateIndex * 2);
                                let lng = -78.71 + (hiddenStateIndex * .6);
                                label.latitude = lat;
                                label.longitude = lng;
                            }
                        } else if(window.innerWidth < 768) {
                            label.fontSize = '.7rem';
                            label.width = 32;
                            label.height = 19;
                            label.paddingTop = 11;
                            label.paddingLeft = 16;

                            if(hiddenStateIndex < 5) {
                                let lat = 35.80 + (hiddenStateIndex * 1.6);
                                let lng = -71.10 + (hiddenStateIndex * .6);
                                label.latitude = lat;
                                label.longitude = lng;
                            } else {
                                let lat = 26.93 + (hiddenStateIndex * 1.6);
                                let lng = -77.71 + (hiddenStateIndex * .45);
                                label.latitude = lat;
                                label.longitude = lng;
                            }
                        } else if(window.innerWidth < 992) {
                            label.fontSize = '.7rem';
                            label.width = 32;
                            label.height = 19;
                            label.paddingTop = 11;
                            label.paddingLeft = 16;

                            if(hiddenStateIndex < 5) {
                                let lat = 36.93 + (hiddenStateIndex * 1.1);
                                let lng = -70.51 + (hiddenStateIndex * .4);
                                label.latitude = lat;
                                label.longitude = lng;
                            } else {
                                let lat = 30.93 + (hiddenStateIndex * 1.1);
                                let lng = -75.71 + (hiddenStateIndex * .35);
                                label.latitude = lat;
                                label.longitude = lng;
                            }
                        } else if(window.innerWidth >= 992) {
                            label.fontSize = '.9rem';
                            label.width = 32;
                            label.height = 19;
                            label.paddingTop = 11;
                            label.paddingLeft = 16;

                            if(hiddenStateIndex < 5) {
                                let lat = 36.93 + (hiddenStateIndex * 1.1);
                                let lng = -70.51 + (hiddenStateIndex * .4);
                                label.latitude = lat;
                                label.longitude = lng;
                            } else {
                                let lat = 30.93 + (hiddenStateIndex * 1.1);
                                let lng = -75.71 + (hiddenStateIndex * .35);
                                label.latitude = lat;
                                label.longitude = lng;
                            }
                        }

                        label.events.on("over", e => {
                            polygon.isHover = true;
                            // This is needed so that "unhovering" previous polygon does not hide tooltip
                            e.target.isHover = false;
                            e.target.isHover = true;
                        });

                        label.events.on("out", e => {
                            polygon.isHover = false;
                        });

                        if(window.innerWidth >= 1024) {
                            label.events.on("over", e => {
                                label.url = polygon.dataItem.dataContext.url;
                                label.urlTarget = 'blank';
                            });

                            label.events.on("out", e => {
                                label.url = null;
                                label.urlTarget = null;
                            });
                        } else {
                            label.userClassName = 'c-svg-map__disable-clicks';
                        }
                    } else {
                        label.latitude = polygon.visualLatitude;
                        label.longitude = polygon.visualLongitude;
                        label.children.getIndex(0).text = stateAbbrev;
                    }

                    if(window.innerWidth >= 1024) {
                        polygon.events.on('over', () => {
                            polygon.url = polygon.dataItem.dataContext.url;
                            polygon.urlTarget = 'blank';
                        });

                        polygon.events.on('out', () => {
                            polygon.url = null;
                            polygon.urlTarget = null;
                        });
                    }

                    var hoverState = polygon.states.create("hover");
                    hoverState.properties.fill = am4core.color(polygon.fill).lighten(-0.35);

                    polygon.events.on("focus", e => {
                        polygon.url = polygon.dataItem.dataContext.url;
                        polygon.urlTarget = 'blank';
                    }, this);

                    polygon.events.on("blur", e => {
                        polygon.url = null;
                        polygon.urlTarget = null;
                    }, this);
                });
            });

            this.$nextTick(() => {
                // Clean up extraneous WAI-ARIA attributes for accessibility & Hide AMCharts Logo
                let svgContainer = this.$refs.chartdiv.querySelector('.c-svg-map svg');
                let toolTips = this.$refs.chartdiv.querySelectorAll('[role="tooltip"]');
                let amChartsLogo = this.$refs.chartdiv.querySelector('g[aria-labelledby^="id-"');

                if(svgContainer) {
                    svgContainer.removeAttribute('role');
                }

                if(toolTips.length > 0) {
                    toolTips.forEach(el => {
                        if(!el.getAttribute('font-size')) {
                            el.parentNode.removeChild(el);
                        }
                    })
                }

                if(amChartsLogo) {
                    amChartsLogo.setAttribute('visibility', 'hidden');
                }
            });

            this.chart = chart;
        },
        selectState() {
            let statePolylgon = this.chart.series.getIndex(0).getPolygonById(this.$refs.stateselector.value);

            if(statePolylgon) {
                statePolylgon.isFocused = true;
                let stateDOMElement = this.$refs.chartdiv.querySelector(`#${statePolylgon.uid}`);

                if (stateDOMElement) {
                    window.setTimeout( () => {
                        // SVG element focus hack for IE
                        if (window.document.documentMode) {
                            let foreignObjectEl = document.createElement('foreignObject');
                            let inputEl = document.createElement('input');
                            foreignObjectEl.appendChild(inputEl);
                            stateDOMElement.appendChild(foreignObjectEl);
                            inputEl.focus();
                            inputEl.disabled = true;
                            foreignObjectEl.parentNode.removeChild(foreignObjectEl);
                        } else {
                            stateDOMElement.focus();
                        }
                    }, 500);
                }
            }
        },
    }
}
</script>



// WEBPACK FOOTER //
// src/app/components/ecc/svg-map.vue